'use client';

import * as React from 'react';
import { Check, ChevronsUpDown, Plus, X } from 'lucide-react';

import { Badge } from 'src/components/ui/badge';
import { Button } from 'src/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from 'src/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from 'src/components/ui/popover';
import { cn } from 'src/utils/utils';
import { selectCurrentPlatformIntegrationsMap } from 'src/redux/selectors/integrations';
import { useSelector } from 'src/redux/store';
import { selectCurrentWorkspaceId } from 'src/redux/slices/userSlice';
import { WidgetType, WidgetTypeConfig } from 'src/types';
import { doc, setDoc } from 'firebase/firestore';
import { DB } from 'src/contexts/FirebaseContext';
import { getCollection } from 'src/api';
import { Collection } from 'src/enum';
import { TagType } from 'src/components/review-tags/tag-selector';
import { keyBy } from 'lodash';

// Sample tags - replace with your actual data source

type Tag = {
  id: string;
  label: string;
};

interface TagSelectorProps {
  widgetId: string;
  placeholder?: string;
  disabled?: boolean;
  maxTags?: number;
  allowCreation?: boolean;
}

export function TagSelector({
  widgetId,
  disabled = false,
  maxTags = Number.POSITIVE_INFINITY,
  allowCreation = true,
}: TagSelectorProps) {
  const [open, setOpen] = React.useState(false);
  const workspaceId = useSelector(selectCurrentWorkspaceId);
  const integrationMap = useSelector(selectCurrentPlatformIntegrationsMap);
  const currentWidget = integrationMap[widgetId];
  // @ts-ignore
  const tags = (currentWidget?.config?.tags || []) as string[];
  const [inputValue, setInputValue] = React.useState('');
  const availableTags = useSelector((state) => state.workspaceReview.tags);
  const availableTagsMap = keyBy(availableTags, 'id');
  //   const [availableTags, setAvailableTags] = React.useState<Tag[]>([]);
  //   const [selected, setSelected] = React.useState<Tag[]>(selectedTags);
  console.log({ tags, availableTags, availableTagsMap });

  const handleSelect = (tag: Tag) => {
    console.log({ tags, tag });

    const isExistingTag = tags.some((t) => t === tag.id);
    const updatedTags = isExistingTag
      ? tags.filter((t) => t !== tag.id)
      : [...tags, tag.id].slice(0, maxTags);

    updateWidgetConfig({ tags: updatedTags });
    setInputValue('');
  };

  const updateWidgetConfig = async (config: Partial<WidgetTypeConfig<WidgetType>>) => {
    try {
      if (!workspaceId) {
        throw new Error('Workspace ID not found');
      }
      await setDoc(
        doc(DB, getCollection[Collection.USERS_INTEGRATIONS](workspaceId), currentWidget.id),
        { config },
        { merge: true }
      );
      //   enqueueSnackbar(t('general.success.default'), { variant: 'success' });
    } catch (error) {
      //   enqueueSnackbar(t('general.error.default'), { variant: 'error' });
    }
  };

  const handleCreateTag = React.useCallback(() => {
    if (!inputValue.trim() || !allowCreation) return;

    const newTagName = inputValue.trim();
    const existingTag = availableTags.find(
      (tag) => tag.label.toLowerCase() === newTagName.toLowerCase()
    );

    if (existingTag) {
      handleSelect(existingTag);
    }

    setInputValue('');
  }, [inputValue, allowCreation, availableTags, handleSelect]);

  //   const handleRemoveTag = React.useCallback(
  //     (tagId: string) => {
  //       updateWidgetConfig({
  //         // @ts-ignore
  //         tags: tags.filter((tag) => tag.id !== tagId),
  //       });
  //     },
  //     []
  //   );

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' && inputValue) {
        e.preventDefault();
        handleCreateTag();
      }
    },
    [handleCreateTag, inputValue]
  );

  const filteredTags = React.useMemo(
    () => availableTags.filter((tag) => tag.label.toLowerCase().includes(inputValue.toLowerCase())),
    [availableTags, inputValue]
  );

  const isMaxTagsReached = tags.length >= maxTags;

  return (
    <div className="flex flex-col gap-1.5">
      <Popover open={open && !disabled} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled || isMaxTagsReached}
            className={cn('w-full justify-between', !tags.length && 'text-muted-foreground')}
          >
            {tags.length > 0
              ? tags.map((tag) => availableTagsMap[tag]?.label).join(', ')
              : 'Select tags'}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full p-0" align="start">
          <Command onKeyDown={handleKeyDown}>
            <CommandInput
              placeholder="Search review tags..."
              value={inputValue}
              onValueChange={setInputValue}
            />
            <CommandList>
              <CommandEmpty className="flex justify-center text-sm py-2">
                {/* {allowCreation && inputValue ? (
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
                    onClick={handleCreateTag}
                  >
                    <Plus className="h-4 w-4" />
                    Create tag "{inputValue}"
                  </button>
                ) : ( */}
                No tags found.
                {/* )} */}
              </CommandEmpty>
              <CommandGroup>
                {filteredTags.map((tag) => {
                  const isSelected = tags.some((s) => s === tag.id);
                  return (
                    <CommandItem key={tag.id} value={tag.label} onSelect={() => handleSelect(tag)}>
                      <Check
                        className={cn('mr-2 h-4 w-4', isSelected ? 'opacity-100' : 'opacity-0')}
                      />
                      {tag.label}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* {tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-1.5">
          {tags.map((tag) => (
            <Badge key={tag.id} variant="secondary" className="gap-1">
              {tag.label}
              <button
                type="button"
                className="ml-1 rounded-full outline-none ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2"
                onClick={() => handleRemoveTag(tag.id)}
              >
                <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                <span className="sr-only">Remove {tag.label}</span>
              </button>
            </Badge>
          ))}
        </div>
      )} */}
    </div>
  );
}
